import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import date from 'date-and-time';
import { faArrowTrendUp } from '@fortawesome/pro-light-svg-icons/faArrowTrendUp';
import { TypedMemo } from '../../../common/types';
import { usePlaylistsTrending } from '../../hooks/usePlaylistsTrending';
import { useSMLDatePickerDateParam } from '../../hooks/useSMLDatePickerDateParam';
import { useSMLPaginationParam } from '../../hooks/useSMLPaginationParam';
import { SMLResultsCard } from '../SMLResultsCard/SMLResultsCard';
import { SMLResultsItem } from '../SMLResultsCard/props';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { datePickerParamName } from '../SMLDatePicker/consts';
import { IntegrationTypeIcon } from '../IntegrationTypeIcon';
import { ValueChangeElement } from '../PositionChangeElement';

const paginationParamName = 'trendingPage';

const TrendingPlaylistsCardComponent: React.FC = () => {
  const intl = useIntl();
  const { selectedDate, selectedDateValue } = useSMLDatePickerDateParam();
  const { currentPage } = useSMLPaginationParam(paginationParamName);
  const {
    data: playlistsData,
    isLoading,
    error,
    refetch,
  } = usePlaylistsTrending({
    page: currentPage,
    date: selectedDate,
  });

  const playlists = useMemo(() => playlistsData?.playlists ?? [], [playlistsData?.playlists]);

  const items: SMLResultsItem[] = useMemo(
    () =>
      playlists.map((playlist) => ({
        id: playlist.playlistId,
        name: playlist.name,
        position: playlist.position,
        imageUrl: playlist.imageUrl,
        rightElement: (
          <div className="d-flex align-items-center" style={{ gap: '8px' }}>
            <IntegrationTypeIcon integrationType={playlist.integrationType} size={20} />
            <ValueChangeElement currentValue={playlist.followersGain} />
          </div>
        ),
        chart: { entityType: 'playlist' as const, date: selectedDate },
        route: SMLRoutes.playlistDetails,
        params: { playlistId: playlist.playlistId, [datePickerParamName]: selectedDateValue },
      })),
    [playlists, selectedDate, selectedDateValue]
  );

  const title = intl.formatMessage({
    id: 'app.sml.playlists.trending.title',
    defaultMessage: 'Trending',
  });

  const subtitle = date.format(selectedDate, 'MMM DD, YYYY');

  return (
    <SMLResultsCard
      title={title}
      subtitle={subtitle}
      icon={faArrowTrendUp}
      iconColor="var(--pos)"
      items={items}
      isLoading={isLoading}
      error={error}
      onRetry={() => {
        refetch().catch(console.error);
      }}
      imageShape="rounded-square"
      showCountryFilter
      paginationParamName={paginationParamName}
    />
  );
};

export const TrendingPlaylistsCard = TypedMemo(TrendingPlaylistsCardComponent);
